import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Copy, Eye, EyeOff, ExternalLink, KeyRound, Loader2, Lock, Pencil, Plus, Trash2, History } from "lucide-react";
import { API_BASE, formatApiError } from "./AuthContext";
import VaultUnlock from "./VaultUnlock";

const blank = { platform: "", url: "", username: "", password: "", notes: "" };
const inputCls = "w-full bg-white border border-[var(--border)] rounded px-2.5 py-1.5 text-[12px] focus:outline-none focus:border-[var(--ink)]";
const copy = async (text, what) => { try { await navigator.clipboard.writeText(text); toast.success(`${what} copied`); } catch { toast.error("Couldn't copy"); } };

function EntryForm({ initial, onCancel, onSave }) {
  const [f, setF] = useState({ ...blank, ...initial, password: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = { platform: f.platform.trim(), url: f.url || null, username: f.username || null, notes: f.notes || null };
    if (f.password || !initial) body.password = f.password || null;
    try { await onSave(body); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="card p-3 space-y-2 bg-[var(--subtle)]" data-testid="vault-entry-form">
      <div className="grid grid-cols-2 gap-2">
        <input required placeholder="Platform (e.g. ATO Online, Xero, MyGov)" value={f.platform} onChange={set("platform")} className={inputCls} data-testid="vault-form-platform" />
        <input placeholder="Website" value={f.url || ""} onChange={set("url")} className={inputCls} data-testid="vault-form-url" />
        <input placeholder="Username / email" value={f.username || ""} onChange={set("username")} className={inputCls} autoComplete="off" data-testid="vault-form-username" />
        <input type="password" placeholder={initial?.has_password ? "New password (blank = keep)" : "Password"} value={f.password} onChange={set("password")} className={inputCls} autoComplete="new-password" data-testid="vault-form-password" />
      </div>
      <textarea placeholder="Notes (security questions, MFA device, who to call…)" value={f.notes || ""} onChange={set("notes")} rows={2} className={inputCls} data-testid="vault-form-notes" />
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="btn-secondary !py-1 text-[12px]" data-testid="vault-form-cancel">Cancel</button>
        <button type="submit" disabled={busy || !f.platform.trim()} className="btn-primary !py-1 text-[12px]" data-testid="vault-form-save">
          {busy && <Loader2 size={12} className="animate-spin" />} Save
        </button>
      </div>
    </form>
  );
}

function EntryRow({ e, clientId, onEdit, onDeleted }) {
  const [pw, setPw] = useState(null);
  const reveal = async () => (await axios.post(`${API_BASE}/clients/${clientId}/vault/${e.entry_id}/reveal`)).data.password;
  const toggle = async () => { if (pw !== null) { setPw(null); return; } try { setPw(await reveal()); } catch (err) { toast.error(formatApiError(err)); } };
  const copyPw = async () => { try { await copy(pw ?? (await reveal()), "Password"); } catch (err) { toast.error(formatApiError(err)); } };
  const del = async () => {
    if (!window.confirm(`Delete the ${e.platform} login?`)) return;
    try { await axios.delete(`${API_BASE}/clients/${clientId}/vault/${e.entry_id}`); toast.success("Deleted"); onDeleted(); } catch (err) { toast.error(formatApiError(err)); }
  };
  const href = e.url && (/^https?:\/\//i.test(e.url) ? e.url : `https://${e.url}`);
  return (
    <li className="card p-3" data-testid={`vault-entry-${e.entry_id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[14px] font-medium flex items-center gap-1.5"><KeyRound size={12} className="text-[var(--accent)]" /> {e.platform}</div>
          {href && <a href={href} target="_blank" rel="noreferrer noopener" className="text-[11px] text-[var(--ink-3)] hover:text-[var(--accent)] inline-flex items-center gap-0.5 truncate" data-testid={`vault-entry-url-${e.entry_id}`}>{e.url} <ExternalLink size={9} /></a>}
        </div>
        <div className="flex gap-1 shrink-0">
          <button onClick={() => onEdit(e)} className="p-1 text-[var(--ink-3)] hover:text-[var(--ink)]" data-testid={`vault-entry-edit-${e.entry_id}`}><Pencil size={12} /></button>
          <button onClick={del} className="p-1 text-[var(--ink-3)] hover:text-[#B91C1C]" data-testid={`vault-entry-delete-${e.entry_id}`}><Trash2 size={12} /></button>
        </div>
      </div>
      <dl className="grid grid-cols-[80px_1fr] gap-y-1 mt-2 text-[12px]">
        <dt className="text-[var(--ink-3)]">Username</dt>
        <dd className="font-mono flex items-center gap-1.5 min-w-0"><span className="truncate" data-testid={`vault-entry-username-${e.entry_id}`}>{e.username || "—"}</span>
          {e.username && <button onClick={() => copy(e.username, "Username")} className="text-[var(--ink-3)] hover:text-[var(--ink)]" data-testid={`vault-entry-copy-user-${e.entry_id}`}><Copy size={11} /></button>}</dd>
        <dt className="text-[var(--ink-3)]">Password</dt>
        <dd className="font-mono flex items-center gap-1.5">
          {e.has_password ? (<>
            <span data-testid={`vault-entry-password-${e.entry_id}`}>{pw ?? "••••••••••"}</span>
            <button onClick={toggle} className="text-[var(--ink-3)] hover:text-[var(--ink)]" data-testid={`vault-entry-reveal-${e.entry_id}`}>{pw !== null ? <EyeOff size={11} /> : <Eye size={11} />}</button>
            <button onClick={copyPw} className="text-[var(--ink-3)] hover:text-[var(--ink)]" data-testid={`vault-entry-copy-pw-${e.entry_id}`}><Copy size={11} /></button>
          </>) : "—"}
        </dd>
        {e.notes && (<><dt className="text-[var(--ink-3)]">Notes</dt><dd className="whitespace-pre-wrap">{e.notes}</dd></>)}
      </dl>
      <div className="text-[10px] text-[var(--ink-3)] mt-1.5">Updated {new Date(e.updated_at).toLocaleString("en-AU")} by {e.updated_by_name}</div>
    </li>
  );
}

function AccessLog({ clientId }) {
  const [rows, setRows] = useState(null);
  const load = () => axios.get(`${API_BASE}/clients/${clientId}/vault-audit`).then((r) => setRows(r.data)).catch(() => setRows([]));
  if (rows === null) return <button onClick={load} className="text-[11px] text-[var(--ink-3)] hover:text-[var(--ink)] inline-flex items-center gap-1" data-testid="vault-show-audit"><History size={11} /> Show access log</button>;
  return (
    <div data-testid="vault-audit-log">
      <div className="overline text-[10px] mb-1">Access log</div>
      <ul className="text-[11px] text-[var(--ink-2)] space-y-0.5">
        {rows.length === 0 && <li className="italic text-[var(--ink-3)]">No activity yet.</li>}
        {rows.map((a) => <li key={a.audit_id} className="font-mono">{new Date(a.at).toLocaleString("en-AU")} · {a.user_name} · {a.action.replace(/_/g, " ")}</li>)}
      </ul>
    </div>
  );
}

// v68.52 — per-client credentials vault (client drawer "Vault" tab).
export default function ClientVaultPanel({ clientId }) {
  const [status, setStatus] = useState(null);
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);   // null | {} new | entry

  const load = useCallback(async () => {
    const s = (await axios.get(`${API_BASE}/vault/status`)).data;
    setStatus(s);
    if (s.unlocked) setRows((await axios.get(`${API_BASE}/clients/${clientId}/vault`)).data);
  }, [clientId]);
  useEffect(() => { load().catch((e) => toast.error(formatApiError(e))); }, [load]);
  useEffect(() => {
    // If the unlock lapses mid-session, show the unlock card again.
    const id = axios.interceptors.response.use(undefined, (err) => {
      if (err?.response?.status === 403 && err.response.data?.detail?.code === "vault_locked") setStatus((s) => (s ? { ...s, unlocked: false } : s));
      return Promise.reject(err);
    });
    return () => axios.interceptors.response.eject(id);
  }, []);

  const save = async (body) => {
    try {
      if (editing.entry_id) await axios.patch(`${API_BASE}/clients/${clientId}/vault/${editing.entry_id}`, body);
      else await axios.post(`${API_BASE}/clients/${clientId}/vault`, body);
      toast.success("Saved — encrypted"); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const lock = async () => { await axios.post(`${API_BASE}/vault/lock`); setRows([]); load(); };

  if (!status) return <div className="p-5 text-[12px] text-[var(--ink-3)]"><Loader2 size={12} className="inline animate-spin" /> Loading…</div>;
  if (!status.unlocked) return <div className="p-5"><VaultUnlock status={status} onUnlocked={load} /></div>;
  return (
    <div className="p-5 space-y-3" data-testid="client-vault-panel">
      <div className="flex items-center justify-between">
        <div className="text-[11px] text-[var(--ink-3)]">Encrypted at rest. Every password reveal is logged.</div>
        <button onClick={lock} className="btn-secondary !py-1 !px-2.5 text-[12px]" data-testid="vault-lock-btn"><Lock size={12} /> Lock vault</button>
      </div>
      {editing ? <EntryForm initial={editing.entry_id ? editing : null} onCancel={() => setEditing(null)} onSave={save} />
        : <button onClick={() => setEditing({})} className="btn-secondary text-[12px]" data-testid="vault-add-btn"><Plus size={12} /> Add login</button>}
      <ul className="space-y-2">
        {rows.length === 0 && !editing && <li className="text-[12px] text-[var(--ink-3)] italic text-center py-4">No saved logins for this client yet.</li>}
        {rows.map((e) => <EntryRow key={e.entry_id} e={e} clientId={clientId} onEdit={setEditing} onDeleted={load} />)}
      </ul>
      <AccessLog clientId={clientId} />
    </div>
  );
}
